import { useEffect, useMemo, useState } from 'react';
import { getBootstrapData, UnifiedSearchableSelect } from '@tpct/admin-ui';
import type { UnifiedSearchableSelectOption } from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';
import type { User } from '@/users/src/types/user';
import { fetchUsersList } from '../utils/api';

const MANUAL_DOSSIER_ATTRIBUTION_ROLES = new Set([
  'collaborateur_comptable',
  'apprenti',
  'stagiaire',
  'expert_comptable',
]);

type CompanyDossierAccessPickerProps = {
  bootstrapKey: string;
  value: string[];
  onChange: (userIds: string[]) => void;
  disabled?: boolean;
  idPrefix?: string;
  description?: string;
};

const CompanyDossierAccessPicker = ({
  bootstrapKey,
  value,
  onChange,
  disabled = false,
  idPrefix = 'company-dossier-access',
  description,
}: CompanyDossierAccessPickerProps) => {
  const dossierAttributionManual = useMemo(() => {
    try {
      const bs = getBootstrapData(bootstrapKey);
      return bs.dossierAttributionDefaultScopeMode === 'explicit';
    } catch {
      return false;
    }
  }, [bootstrapKey]);

  const [pickerUsers, setPickerUsers] = useState<User[]>([]);
  const [pickerLoadState, setPickerLoadState] = useState<'idle' | 'loading' | 'error'>('idle');

  const attributionOptions: UnifiedSearchableSelectOption[] = useMemo(
    () =>
      pickerUsers
        .filter((u) => MANUAL_DOSSIER_ATTRIBUTION_ROLES.has(u.role))
        .map((u) => ({
          value: String(u.id),
          label: u.name,
          searchText: `${u.name} ${u.role}`,
        })),
    [pickerUsers],
  );

  useEffect(() => {
    if (!dossierAttributionManual) {
      return undefined;
    }
    let cancelled = false;
    setPickerLoadState('loading');
    fetchUsersList(bootstrapKey)
      .then((res) => {
        if (!cancelled) {
          setPickerUsers(res.users);
          setPickerLoadState('idle');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPickerLoadState('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [bootstrapKey, dossierAttributionManual]);

  if (!dossierAttributionManual) {
    return null;
  }

  return (
    <div className="form-section">
      <h4 className="company-form__section-title">{__('Accès au dossier')}</h4>
      <div className="form-field">
        <p className="description">
          {description ??
            __('Choisissez les membres du cabinet qui auront accès à ce dossier.')}
        </p>
        {pickerLoadState === 'error' ? (
          <div className="notice notice-error">
            <p>{__('Impossible de charger la liste des utilisateurs.')}</p>
          </div>
        ) : null}
        <UnifiedSearchableSelect
          id={`${idPrefix}-users`}
          options={attributionOptions}
          multiple
          searchable
          disabled={disabled || pickerLoadState === 'loading'}
          placeholder={__('Rechercher et sélectionner des utilisateurs…')}
          searchPlaceholder={__('Rechercher…')}
          noResultsText={__('Aucune option trouvée')}
          value={value}
          onChange={onChange}
          removeItemAriaLabel={(label) => `${__('Retirer')} ${label}`}
        />
      </div>
    </div>
  );
};

export default CompanyDossierAccessPicker;
